import type { Offer, OfferSettings, OfferType } from '@makemydeal/dr-platform-types';
import { FINANCE, LEASE } from '@makemydeal/dr-platform-types';
import type { PartialPenciledInputsWithAllTypes, PenciledInputsState } from '../../../types/commonTypes';
import * as types from '../../../types/actionTypes';
import { handlePaymentReceived } from '../../../utils/penciledInputsStateUtils';
import { updatePaymentGridLoadingStatuses } from '../../../utils/paymentGridUtils';
import isEmpty from 'lodash.isempty';

/**
 * Releases a grid row's own call after the payment it was waiting on was superseded. A row goes out as
 * `UPDATE_LEASE_TERMS_GRID` or `UPDATE_FINANCE_TERMS_GRID`, which are on no stamp-exempt list, so a later stamped
 * deal-screen edit marks that round's responses stale. The data stays discarded; only the call is released, since the
 * loading state is UI bookkeeping and is otherwise cleared solely by `handlePaymentReceived`, which a discard never
 * reaches. The row keeps loading while the call that superseded this one is still out, and settles with it.
 */
const releaseStaleGridRowLoader = (state: PenciledInputsState, action: any, offerType: OfferType): PenciledInputsState => {
    const paymentGridMeta = action.meta?.originalAction?.meta?.paymentGrid;

    if (!paymentGridMeta?.gridAction || paymentGridMeta?.isFlexBuyPayment || !state.byOfferType) {
        return state;
    }

    const currentOffer = state.byOfferType[offerType];

    return {
        ...state,
        byOfferType: {
            ...state.byOfferType,
            [offerType]: {
                ...currentOffer,
                paymentGridData: updatePaymentGridLoadingStatuses(offerType, currentOffer?.paymentGridData || {}, action, false)
            }
        }
    };
};

export const handlePaymentCases = (
    state: PenciledInputsState,
    action: any,
    offerType: OfferType,
    offer: Offer,
    settings: OfferSettings,
    _isUserProgramQuotes: boolean
): PartialPenciledInputsWithAllTypes => {
    switch (action.type) {
        case types.PAYMENT_RECEIVED: {
            // R-10 latest-call-wins: drop a superseded (stale) response so an out-of-order call can't overwrite
            // the fresh penciled inputs. A grid row's call is released even so - see `releaseStaleGridRowLoader`
            // for why DE761542's stamping exemption does not cover it.
            if (action.meta?.isStalePaymentResponse) {
                return releaseStaleGridRowLoader(state, action, offerType);
            }
            return handlePaymentReceived({ state, action, offerType, offer, settings });
        }
        case types.UPDATED_SECOND_PAYMENT_DUE_DATE_FIELD: {
            if (!state.byOfferType) return state;
            return {
                ...state,
                byOfferType: {
                    ...state.byOfferType,
                    [LEASE]: {
                        ...state.byOfferType?.[LEASE],
                        secondPaymentDueDate: action.payload
                    }
                }
            };
        }

        case types.UPDATED_SPECIAL_PAYMENT_FORMULA: {
            if (!state.byOfferType) return state;
            return {
                ...state,
                byOfferType: {
                    ...state.byOfferType,
                    [FINANCE]: {
                        ...state.byOfferType?.[FINANCE],
                        specialPaymentFormula: action.payload
                    }
                }
            };
        }

        case types.RESET_SPECIAL_PAYMENT_FORMULA: {
            if (!state.byOfferType) return state;
            return {
                ...state,
                byOfferType: {
                    ...state.byOfferType,
                    [FINANCE]: {
                        ...state.byOfferType?.[FINANCE],
                        specialPaymentFormula: undefined,
                        initialPaymentDiscount: undefined
                    }
                }
            };
        }

        case types.UPDATE_FLEX_BUY_PAYMENT: {
            if (offerType !== FINANCE) return state;

            const currentOffer = state.byOfferType?.[offerType];
            const flexBuyPayment: number[] = action.payload;
            if (!state.byOfferType) return state;
            if (!isEmpty(flexBuyPayment)) {
                return {
                    ...state,
                    byOfferType: {
                        ...state.byOfferType,
                        [offerType]: {
                            ...currentOffer,
                            initialPaymentDiscount: flexBuyPayment
                        }
                    }
                };
            }

            return state;
        }

        case types.SET_PREVIOUS_FINANCE_INPUT_WITHOUT_FFB: {
            const currentOffer = state.byOfferType?.[FINANCE];

            return {
                ...state,
                previousFinanceInputWithoutFlexBuy: currentOffer
            };
        }

        case types.RESET_PREVIOUS_FINANCE_INPUT_WITHOUT_FFB: {
            const previousFinanceInputWithoutFlexBuy = state.previousFinanceInputWithoutFlexBuy;
            if (!state.byOfferType || !previousFinanceInputWithoutFlexBuy) return state;
            return {
                ...state,
                previousFinanceInputWithoutFlexBuy: undefined,
                byOfferType: {
                    ...state.byOfferType,
                    [FINANCE]: previousFinanceInputWithoutFlexBuy
                }
            };
        }

        case types.UPDATED_PAYMENT_FREQUENCY: {
            const { frequency, offerType: actionOfferType } = action.payload;
            const targetOfferType = actionOfferType || offerType;
            if (!state.byOfferType) return state;
            return {
                ...state,
                byOfferType: {
                    ...state.byOfferType,
                    [targetOfferType]: {
                        ...state.byOfferType?.[targetOfferType as keyof typeof state.byOfferType],
                        paymentFrequency: frequency
                    }
                }
            };
        }

        default: {
            return state;
        }
    }
};
